import React, {ChangeEvent, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import ExpandableItem from './ExpandableItem';
import ReportItem from './ReportItem';
import DescText from '../../common/component/DescText';
import Header from '../../common/component/Header';
import Modal, {FULL_CONTENT_STYLE} from '../../common/component/Modal';
import {pAlert} from '../../common/component/PAlert';
import Text from '../../common/component/Text';
import TextButton from '../../common/component/TextButton';
import {
  IS_CBT_OR_PROD,
  REGION,
  X_PADDING_ONLY_SMALL_CN,
} from '../../common/constant';
import {pToast} from '../../common/container/PToast';
import {openWindow} from '../../common/function';
import {ButtonSet} from '../../common/type/alert';
import {sendPageView} from '../../common/util/tracking';
import MaxWidth from '../../commonSub/component/MaxWidth';
import {CommentTrackingContext} from '../common';
import useCommentEvent from '../hook/useCommentEvent';
import {actions} from '../state/action';
import {KOR_REPORT_TYPE, ReportType} from '../type';

interface IProps {
  commentId: number;
  onRequestClose: () => void;
}

export default function ReportModal({commentId, onRequestClose}: IProps) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [reportType, setReportType] = useState<ReportType>(ReportType.Spam);
  const [etcText, setEtcText] = useState('');

  const onChangeEtcText = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.currentTarget.value;
    setEtcText(text.substr(0, ETC_TEXT_MAX_LENGTH));
  };

  const onChangeReportType = (type: ReportType) => {
    setReportType(type);
    setEtcText('');
  };

  const {sendCommentEvent} = useCommentEvent('comments_report');
  const onClickReport = () => {
    const onSuccess = async (url?: string) => {
      if (!KOR_REPORT_TYPE.includes(reportType)) {
        pToast(_t => _t('comment_report_submitted_toast'));
        onRequestClose();
        sendCommentEvent({
          actionName: 'report',
          action: 'click',
          customProps: {
            report_category: reportType.replace('comment_report_', ''),
            comment_id: commentId,
          },
        });
      } else if (reportType === ReportType.Right) {
        // TODO: 기획서에 불법 유통 관련 문구 변경이 적혀 있어 일단 Right, Piracy로 분기합니다. 문구가 동일하다면 alert 관련 중복을 제거할 예정입니다.
        const {okClicked} = await pAlert({
          buttonSet: ButtonSet.CancelAndConfirm,
          isTextAlignCenter: false,
          title: t('comment_report_piracy_popup_title'),
          desc: (
            <>
              <DescText className="mt-10 s13-regular-grey08">
                {t('comment_report_piracy_popup_content_1')}
              </DescText>
              <DescText className="mt-10 s13-regular-grey08">
                {t('comment_report_piracy_popup_content_2')}
              </DescText>
            </>
          ),
        });
        if (okClicked) {
          openWindow({
            url: (IS_CBT_OR_PROD ? CS_LANDING_URLS : CS_LANDING_URLS_DEV)[
              reportType
            ],
            windowName: '',
          });
          onRequestClose();
        }
      } else if (reportType === ReportType.Piracy && !!url) {
        const {okClicked} = await pAlert({
          buttonSet: ButtonSet.CancelAndConfirm,
          isTextAlignCenter: false,
          title: t('comment_report_piracy_popup_title'),
          desc: (
            <>
              <DescText className="mt-10 s13-regular-grey08">
                {t('comment_report_piracy_popup_content_1')}
              </DescText>
              <DescText className="mt-10 s13-regular-grey08">
                {t('comment_report_piracy_popup_content_2')}
              </DescText>
            </>
          ),
        });
        if (okClicked) {
          openWindow({
            url,
            windowName: '',
          });
          onRequestClose();
        }
      }
    };
    dispatch(
      actions.fetchReportComment({
        commentId,
        reportType,
        etcText,
        onSuccess,
      })
    );
  };

  const {section, questCampaignId} = useContext(CommentTrackingContext);
  useEffect(() => {
    sendPageView({
      section,
      page: 'comments_report',
      actionName: 'comments_report',
      customProps: {
        comment_id: commentId,
        quest_campaign_id:
          section === 'event_page' ? questCampaignId : undefined,
      },
      isSendExtra: true,
    });
  }, [commentId, section, questCampaignId]);

  const onClose = () => {
    onRequestClose();
    sendCommentEvent({
      actionName: 'back',
      action: 'click',
      customProps: {commend_id: commentId},
    });
  };

  const isReportBtnEnable = reportType !== ReportType.Etc || !!etcText.length;
  const isKorea = REGION === 'kor';

  return (
    <Modal
      contentStyle={FULL_CONTENT_STYLE}
      isOpen={true}
      onRequestClose={onClose}
    >
      <>
        <Header
          className="bg-background-02 light:bg-white"
          title={t('comment_report')}
          buttonIcon="back"
          onClick={onClose}
        />
        <div className="h-header-height" />
        <MaxWidth
          hasMdWidth
          className={cn(
            X_PADDING_ONLY_SMALL_CN,
            'pt-20 pb-100 overflow-y-auto no-scrollbar'
          )}
          onTouchMove={e => e.stopPropagation()}
        >
          <ul>
            {Object.values(ReportType)
              .filter(type => !KOR_REPORT_TYPE.includes(type))
              .map(type => (
                <ReportItem
                  key={type}
                  type={type}
                  commentId={commentId}
                  isSelected={reportType === type}
                  onChange={onChangeReportType}
                />
              ))}
          </ul>
          {reportType === ReportType.Etc && (
            <textarea
              className={cn(
                'mb-30 h-80 w-full p-10 bg-grey-02 no-scrollbar rounded-8',
                'outline-none resize-none border-grey-03',
                's13-regular-white light:text-black leading-20 placeholder-grey06'
              )}
              value={etcText}
              placeholder={t('comment_report_other_inputbox')}
              onChange={onChangeEtcText}
            />
          )}
          {isKorea && (
            <>
              <Divider className="mb-20" />
              <ul>
                <ExpandableItem
                  type={ReportType.Right}
                  isSelected={reportType === ReportType.Right}
                  commentId={commentId}
                  content={t('comment_report_rights_detail')}
                  onChange={onChangeReportType}
                />
                <ExpandableItem
                  type={ReportType.Piracy}
                  isSelected={reportType === ReportType.Piracy}
                  commentId={commentId}
                  content={t('comment_report_piracy_detail')}
                  onChange={onChangeReportType}
                />
              </ul>
            </>
          )}
          <Divider className="mt-24" />
          <Text className="mt-24 s12-regular-grey06">
            {t('comment_report_info')}
          </Text>
        </MaxWidth>
        <div
          className={cn(
            'fixed inset-x-18 bottom-0 flex flex-col justify-center',
            'md:w-[454px] lg:w-[594px] mx-auto'
          )}
        >
          <TextButton
            className={cn(
              'w-full mt-50 mb-30',
              isReportBtnEnable ? 'btn-white light:btn-black' : 'btn-grey-04'
            )}
            textClassName={
              isReportBtnEnable
                ? 's16-bold-black light:text-white'
                : 's16-bold-grey02'
            }
            height="50px"
            onClick={onClickReport}
            disabled={!isReportBtnEnable}
          >
            {t('comment_report_button')}
          </TextButton>
        </div>
      </>
    </Modal>
  );
}

const ETC_TEXT_MAX_LENGTH = 500;

const Divider = ({className}: {className?: string}) => (
  <div className={cn('bg-grey-04 h-1 w-full', className)} />
);

const CS_LANDING_URLS = {
  [ReportType.Right]:
    'https://cs.kakao.com/requests?category=602&locale=ko&service=192',
};
const CS_LANDING_URLS_DEV = {
  [ReportType.Right]:
    'https://cs-new.devel.kakao.com/requests?category=597&locale=ko&service=188',
};
